import { ACTION_ARGS, MODAL_ARGS, Metadata, pick } from '../../../utils/spds/metadata';
import { Dismissible } from './capability/dismissible.gjs';
import { compose } from '../../../utils/spds/capabilities/helpers';
import { service } from '@ember/service';
import { waitForRender } from '../../../utils/waiters';
import Super from './base.gjs';
import dialogPolyfill from 'dialog-polyfill';
import styles from './modal.module.scss';

const METADATA = Metadata.define({
  description: `
The SPDS base class for modals. It houses all common modal behavior, but does not provide a template.

### Modal or overlay?
When inheriting from the \`spds/core/modal\` class, setting the \`modal\` attribute to \`true\` will invoke the dialog element's
\`showModal\` function on opening the dialog, which automatically includes the \`::backdrop\` pseudoelement and turns off scrolling within
the \`body\` of the HTML document through the \`bodyScrollManager\` service. That service tracks the lock by owner, so closing the top of
two stacked modals leaves the page held for the one still open. If \`modal\` is instead set to \`false\` or not set, then it will
appear as an overlay above the rest of the page, which will remain interactive.

### Escape
Escape on a modal dialog raises \`cancel\`, which this class prevents and routes through the same guarded path as every other
dismissal, so \`onBeforeClose\` gets its say. That path is not optional — \`closeOnEscape\` does not reach it. A non-modal dialog is
shown with \`show\`, which the browser never dismisses, so it handles the key itself and \`closeOnEscape\` is the opt-out there; see
\`Spds::Modal::Flyout\`. An Escape already claimed by something
nested inside the dialog is left alone, so a menu open within it closes without taking the dialog with it.

### Polyfill
Since SPDS modals rely on the dialog element, which is only recently widely supported, the \`core/modal\` class can conditionally
fall back on a [polyfill provided by the Google Chrome team](https://github.com/GoogleChrome/dialog-polyfill) in cases where users
are accessing our apps through an outdated browser.

### Hooks
The open/close lifecycle comes from the \`Dismissible\` capability, which exposes these hooks:

* \`onBeforeClose\`: this hook can be used to catch a user request to close the modal and execute custom logic to block or allow
the close process to continue. If the provided handler returns a truthy value, then closing will continue. If a falsey value is provided,
then the modal will remain open
* \`onClose\`: this hook can be used to implement custom behavior on modal close, such as user interaction tracking, or route transition
* \`onOpen\`: this hook can be used to implement custom behavior on modal open, such as user interaction tracking
* \`onShow\`: the same, but deferred until the dialog is actually on screen, so it can measure or move focus

### Setup
All modal setup runs in the \`setup\` hook invoked by the core/base \`setupComponent\` modifier: polyfill registration and event
listeners here, then the capability's open sequence, which calls back into \`showElement\` to put the dialog on screen. Subclasses
that override \`setup\` should call \`super.setup()\` first to run the default modal behavior, then add their own logic. The
\`modal\` attribute on the inheriting component determines whether the dialog uses
\`showModal\` (with backdrop) or \`show\` (overlay). When \`showModal()\` runs, the instance registers with \`spds/modal\` service
\`openModalStack\` (last = topmost) and unregisters in \`willDestroy\` when the instance is torn down.
  `,
  extends: [
    pick(ACTION_ARGS, [{ name: 'onClose', required: true }]),
    pick(MODAL_ARGS, ['closeOnClickOutside']),
  ],
  name: 'Modal',
});

/**
 * Base class for SPDS modal variants. The open/close lifecycle, its consumer hooks, and the
 * `@animated` argument come from the `Dismissible` capability; the arguments below are the ones
 * this class adds or tightens.
 *
 * Arguments
 * @argument {boolean} closeOnClickOutside controls whether the modal should automatically close when user clicks outside of its DOM tree
 * @argument {function} onClose gives a hook to perform custom behavior when the modal closes; required on modals
 */
export default class SpdsCoreModal extends compose(Super, Dismissible) {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseStyles = styles;

  /**
   * Whether the gesture in progress both began and ended on the backdrop. A `click` is reported on
   * the nearest common ancestor of its press and release, so a drag out of the panel is reported on
   * the dialog exactly as a backdrop click is; this is what tells them apart.
   * @private
   */
  #pressOnBackdrop = false;

  @service bodyScrollManager;
  @service fastboot;
  @service('spds/modal') spdsModal;

  get closeOnClickOutside() {
    return this.argOrDefault('closeOnClickOutside');
  }

  /**
   * Only consulted for a dialog shown without a backdrop. A modal one receives Escape as the
   * browser's `cancel` event, which is routed through `beforeClose` whatever this is set to, so
   * setting it false there disables nothing — `@onBeforeClose` is what guards that path.
   * `Spds::Modal::Flyout` is the one variant that can be non-modal and is where the argument is
   * declared; another non-modal variant has to declare it too to be opted out of.
   */
  get closeOnEscape() {
    return this.argOrDefault('closeOnEscape');
  }

  /**
   * willDestroy lifecycle cleans up remaining modal logic,
   * allowing body scrolling to resume and removing the click handler
   * for closing on click outside if it was added
   */
  willDestroy() {
    super.willDestroy(...arguments);

    this.spdsModal.unregisterOpenModal(this.id);

    // Unconditional: releasing an owner that never claimed is a no-op, which covers both a
    // non-modal dialog that never locked and FastBoot, where nothing claimed at all.
    this.bodyScrollManager.unlockScroll(this.id);

    if (this.modal && this.closeOnClickOutside) {
      this.baseElement?.removeEventListener('mousedown', this.handleMouseDownOnDialog);
      this.baseElement?.removeEventListener('mouseup', this.handleMouseUpOnDialog);
      this.baseElement?.removeEventListener('click', this.handleClickOnDialog);
    }

    // Unguarded because a dialog torn down before it ever closed still has the listener attached,
    // and removing one that was never added is a no-op either way.
    this.baseElement?.removeEventListener('keydown', this.handleEscape);
  }

  /**
   * private function for cleaning up event handlers on teardown
   */
  #cleanupListeners(cancel, close) {
    this.baseElement.removeEventListener('cancel', cancel);
    this.baseElement.removeEventListener('close', close);
    this.baseElement.removeEventListener('keydown', this.handleEscape);
  }

  /**
   * private function for setting up modal-specific functionality
   * instantiates dialog element with backdrop through `showModal` and
   * prevents body scrolling through the `bodyScrollManager.lockScroll` function
   */
  #modalSetup() {
    this.baseElement.showModal();

    this.spdsModal.registerOpenModal(this.id);

    if (this.fastboot?.isFastBoot) return;

    this.bodyScrollManager.lockScroll(this.id);
  }

  /**
   * The capability's hook for displaying the element. A dialog is not shown by
   * the `is-visible` class alone — every variant stylesheet gates that class
   * behind `[open]` — so this adds the attribute through `show`/`showModal`.
   *
   * The wait beforehand is what makes the variant SCSS entry transition
   * (flyout slide-in, full-screen slide-up, standard fade-in) actually fire:
   * the browser needs the laid-out off-screen state committed to use as the
   * transition's "from" value when `[open]` lands. Showing in the same paint
   * frame as the initial element insertion gives it nothing to transition
   * from and the dialog appears instantly.
   *
   * The `try/catch` covers the rare case where the element is disconnected
   * during the `await waitForRender()` (parent re-renders this component out
   * of the DOM mid-open). In that case `show`/`showModal` would throw on a
   * disconnected element, but the component is already being destroyed so
   * silently bailing is correct.
   */
  async showElement() {
    if (!this.baseElement) return;

    await waitForRender();

    // A dismissal that landed during the wait has already run its course, and `close` is latched
    // against running twice. Adding `[open]` now would strand the dialog on screen with no way out.
    if (this.isClosing) return;

    try {
      if (this.modal) {
        this.#modalSetup();
      } else {
        this.baseElement.show();
      }
    } catch {
      // silently fail
    }
  }

  /**
   * Ensures this.baseElement is set and registers the dialog polyfill when needed.
   * Subclasses that override setup() should call this first.
   * @param {HTMLDialogElement} [element] - Dialog element; defaults to this.baseElement when called from setup().
   */
  runModalSetup() {
    if (!this.baseElement) return;

    if (!this.fastboot?.isFastBoot && !window.HTMLDialogElement) {
      dialogPolyfill.registerDialog(this.baseElement);
      this.variantClass = 'spds-dialog-polyfill-modal';
    }

    // Routed through the public `beforeClose` so the browser's own dismissal — Escape on a native
    // modal dialog — picks up the same `onBeforeClose` check and re-entrancy guards as the
    // keyboard and click-outside paths.
    let cancelListener = async evt => {
      evt.preventDefault();
      await this.beforeClose(evt);
    };

    let closeListener = async () => {
      this.#cleanupListeners(cancelListener, closeListener);

      // A dialog can close without passing through `close` at all — a `form method="dialog"`
      // submit, say. Latching the dismissal here is what keeps a later `close` or `beforeClose`
      // from replaying the sequence against a dialog that has already shut and reporting a second
      // `onClose`. The token is outstanding only while a dismissal is in flight, since `show`
      // releases the opening one, so its release goes through the capability's guarded form.
      this.markDismissed();
      this.settleToken();
      this.reportClose();
    };

    this.baseElement.addEventListener('cancel', cancelListener);
    this.baseElement.addEventListener('close', closeListener);

    // `mousedown`/`mouseup` rather than their pointer equivalents: `@ember/test-helpers` `click()`
    // fires no pointer events, so a pointer-keyed guard would never open and every dismissal would
    // be blocked under test. Touch still works, since a tap fires the compatibility mouse events.
    if (this.modal && this.closeOnClickOutside) {
      this.baseElement.addEventListener('mousedown', this.handleMouseDownOnDialog);
      this.baseElement.addEventListener('mouseup', this.handleMouseUpOnDialog);
      this.baseElement.addEventListener('click', this.handleClickOnDialog);
    }

    // A modal dialog gets Escape from the browser as the `cancel` event above. A non-modal one is
    // shown with `show()`, which the browser never dismisses, so the key has to be handled here.
    if (!this.modal && this.closeOnEscape) {
      this.baseElement.addEventListener('keydown', this.handleEscape);
    }
  }

  /**
   * Invoked by setupComponent when the dialog element is in the DOM. Attaches the dialog's own
   * listeners first, so the `close` event is caught however the dialog ends up closing, then hands
   * off to the capability for the open sequence.
   */
  setup() {
    this.runModalSetup();

    super.setup();
  }

  /**
   * Closes the dialog once the exit transition has played. The `close` listener attached in
   * `runModalSetup` settles the closing waiter and fires `@onClose`, so both fall out of the
   * native `close` event rather than being done here.
   *
   * A dialog dismissed before it ever opened is the exception. Without `[open]` to remove, the
   * browser treats `close()` as a no-op and never raises the event, so nothing would settle the
   * waiter or tell the consumer. That dismissal finishes here instead.
   *
   * A dialog shut by something else while the transition was playing reaches the same branch having
   * already reported through the listener, which is why the report goes through `reportClose`.
   */
  dismissElement() {
    if (this.baseElement?.open) {
      this.baseElement.close();

      return;
    }

    this.settleToken();
    this.reportClose();
  }

  handleMouseDownOnDialog = evt => {
    this.#pressOnBackdrop = evt.target === this.baseElement;
  };

  /**
   * Narrows rather than sets, so a gesture that began on the backdrop but ended inside the panel
   * no longer counts — the dismissal has to stay outside from end to end, as it does for a native
   * `popover="auto"`.
   */
  handleMouseUpOnDialog = evt => {
    this.#pressOnBackdrop &&= evt.target === this.baseElement;
  };

  handleClickOnDialog = async evt => {
    if (evt.target === this.baseElement && this.#pressOnBackdrop) {
      await this.beforeClose();
    }
  };
}

export const CORE_MODAL_METADATA = SpdsCoreModal.metadata;
